/** Phase 5 admin view models (mirrors of @hopex/core admin read models; the admin never imports server code). */
import { formatDecimal } from '@hopex/ui';

export type Tone = 'positive' | 'negative' | 'accent' | 'neutral';

export interface ScreeningState {
  state: 'PASS' | 'NEEDS_ACCEPTANCE' | 'BLOCKED';
  reason: string;
  check: {
    id: string;
    result: 'CLEAR' | 'REVIEW' | 'HIGH_RISK' | 'SANCTIONS_HIT' | 'ERROR';
    provider: string;
    providerRef: string | null;
    policyVersion: number;
    categories: string[];
    score: string | null;
    at: string;
    decision: { decision: string; reason: string; at: string } | null;
  } | null;
}

export interface CryptoDepositRow {
  id: string;
  ref: string;
  status:
    | 'ADDRESS_ISSUED'
    | 'EXPIRED_NO_TXID'
    | 'TXID_SUBMITTED'
    | 'UNDER_REVIEW'
    | 'APPROVED_CREDITED'
    | 'REJECTED';
  asset: string;
  network: string;
  networkName: string;
  address: string;
  memo: string | null;
  txid: string | null;
  explorerUrl: string | null;
  lateRegistration: boolean;
  txidSubmittedAt: string | null;
  finalAmount: string | null;
  minimumCheck: 'MET' | 'BELOW' | 'UNKNOWN' | null;
  rejectReason: string | null;
  creditTransactionId: string | null;
  evidence: {
    senders: string[];
    amountOnchain: string;
    blockTime: string | null;
    explorerUrl: string | null;
    note: string | null;
    capturedAt: string;
  } | null;
  screening: ScreeningState;
  createdAt: string;
  userId: string;
  publicId: string | null;
  displayName: string | null;
  version: string;
}

export interface CryptoWithdrawalRow {
  id: string;
  ref: string;
  status: 'PROCESSING' | 'SECURITY_HOLD' | 'COMPLETED' | 'REJECTED_REFUNDED';
  asset: string;
  network: string;
  networkName: string;
  address: string;
  addressExplorerUrl: string | null;
  memo: string | null;
  destAmount: string;
  fee: string;
  total: string;
  feeSource: 'REFERENCE' | 'ADMIN_OVERRIDE';
  payoutTxid: string | null;
  payoutExplorerUrl: string | null;
  holdReason: string | null;
  rejectReason: string | null;
  reserveTransactionId: string | null;
  settleTransactionId: string | null;
  screening: ScreeningState;
  confirmedAt: string | null;
  decidedAt: string | null;
  createdAt: string;
  userId: string;
  publicId: string | null;
  displayName: string | null;
  version: string;
}

export const CRYPTO_DEPOSIT_STATUS: Record<string, { label: string; tone: Tone }> = {
  ADDRESS_ISSUED: { label: 'آدرس صادر شد', tone: 'neutral' },
  EXPIRED_NO_TXID: { label: 'بدون ثبت انتقال', tone: 'neutral' },
  TXID_SUBMITTED: { label: 'TXID ثبت شد', tone: 'accent' },
  UNDER_REVIEW: { label: 'در حال بررسی', tone: 'accent' },
  APPROVED_CREDITED: { label: 'تأیید و اعتبار', tone: 'positive' },
  REJECTED: { label: 'رد شد', tone: 'negative' },
};

export const CRYPTO_WITHDRAWAL_STATUS: Record<string, { label: string; tone: Tone }> = {
  PROCESSING: { label: 'در حال انجام', tone: 'accent' },
  SECURITY_HOLD: { label: 'توقف امنیتی', tone: 'negative' },
  COMPLETED: { label: 'ارسال شد', tone: 'positive' },
  REJECTED_REFUNDED: { label: 'رد و بازگشت', tone: 'neutral' },
};

export const SCREEN_RESULT: Record<string, { label: string; tone: Tone }> = {
  CLEAR: { label: 'بدون ریسک', tone: 'positive' },
  REVIEW: { label: 'نیازمند بررسی', tone: 'accent' },
  HIGH_RISK: { label: 'ریسک بالا', tone: 'negative' },
  SANCTIONS_HIT: { label: 'تطابق تحریم', tone: 'negative' },
  ERROR: { label: 'غربالگری در دسترس نبود', tone: 'negative' },
};

export const GATE_LABEL: Record<ScreeningState['state'], string> = {
  PASS: 'مجاز',
  NEEDS_ACCEPTANCE: 'نیازمند پذیرش صریح',
  BLOCKED: 'مسدود',
};

/** Exact server digits with the asset code (never cut). */
export const exact = (v: string | null | undefined, asset: string) =>
  v === null || v === undefined ? '—' : `${formatDecimal(v, { trimTrailingZeros: true })} ${asset}`;
